import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";

const ViewSegment = () => {
    const navigate = useNavigate();
    const { segmentName } = useParams<{ segmentName: string }>();

    const [searchTerm, setSearchTerm] = useState("");

    // Segment data (same as in manage-customers/index.tsx)
    const segmentData = [
        {
            title: "High-Value Customers",
            type: "Dynamic",
            description: "Customers with lifetime value > AED 5000",
            count: 850,
            status: "Active",
            icon: "💎",
        },
        {
            title: "Cart Abandoners",
            type: "Dynamic",
            description: "Customers with abandoned carts",
            count: 1540,
            status: "Active",
            icon: "🛒",
            campaignMetrics: {
                subject: "Cart Abandonment Campaign",
                campaignType: "Campaign",
                segmentAudience: "Cart Abandoners",
                recipients: 2300,
                delivered: 2210,
                opened: 740,
                clicked: 180,
                overallStatus: "Completed",
                sentAt: "2024-11-25",
                actionsDrilldown: "View Details",
                sent: 2300,
                failed: 90,
                consentIssues: 12,
                revenueInfluenced: "AED X (future)"
            }
        },
        {
            title: "UAE Customers",
            type: "Static",
            description: "Customers based in UAE",
            count: 12500,
            status: "Active",
            icon: "🇦🇪",
        },
        {
            title: "Fashion Customers",
            type: "Dynamic",
            description: "Fashion category Customers",
            count: 5600,
            status: "Active",
            icon: "👗",
        },
        {
            title: "New Signups",
            type: "Dynamic",
            description: "Recently registered Customers",
            count: 890,
            status: "Active",
            icon: "✨",
        },
        {
            title: "Lapsed Customers",
            type: "Predictive",
            description: "Inactive Customers",
            count: 2300,
            status: "Active",
            icon: "⚠️",
        },
    ];

    // Find the current segment
    const currentSegment = segmentData.find(segment => segment.title.toLowerCase().replace(/\s+/g, '-') === segmentName?.toLowerCase().replace(/\s+/g, '-'));

    // Sample customer data (same as in manage-customers/index.tsx)
    const customerData = [
        {
            id: "CUST-00025",
            name: "Maha Sultan",
            email: "maha.sultan@gmail.com",
            phone: "+971507677889",
            status: "Active",
            tier: "Gold",
            health: 92.5,
            orders: 11,
            totalSpent: "AED 7,800.00",
            ltv: "AED 8,600.00",
            lastOrder: "11/25/2024",
        },
        {
            id: "CUST-00024",
            name: "Kareem Bashar",
            email: "kareem.bashar@hotmail.com",
            phone: "+971506566778",
            status: "At-risk",
            tier: "Silver",
            orders: 5,
            health: 70.5,
            totalSpent: "AED 2,400.00",
            ltv: "AED 2,650.00",
            lastOrder: "11/05/2024",
        },
        {
            id: "CUST-00023",
            name: "Samira Adnan",
            email: "samira.adnan@yahoo.com",
            phone: "+971505455667",
            status: "Dormant",
            tier: "Gold",
            orders: 9,
            health: 64.5,
            totalSpent: "AED 6,100.00",
            ltv: "AED 6,750.00",
            lastOrder: "10/30/2024",
        },
        {
            id: "CUST-00022",
            name: "Nader Qasim",
            email: "nader.qasim@gmail.com",
            phone: "+971504344556",
            status: "VIP",
            tier: "Gold",
            orders: 13,
            health: 92.5,
            totalSpent: "AED 9,800.00",
            ltv: "AED 10,800.00",
            lastOrder: "11/28/2024",
        },
        {
            id: "CUST-00021",
            name: "Lina Zayed",
            email: "lina.zayed@outlook.com",
            phone: "+971503233445",
            status: "Flagged",
            tier: "Silver",
            health: 100,
            orders: 7,
            totalSpent: "AED 4,200.00",
            ltv: "AED 4,650.00",
            lastOrder: "11/15/2024",
        },
        {
            id: "CUST-00020",
            name: "Walid Majid",
            email: "walid.majid@gmail.com",
            phone: "+971502122334",
            status: "Inactive",
            tier: "Bronze",
            health: 40.5,
            orders: 1,
            totalSpent: "AED 380.00",
            ltv: "AED 420.00",
            lastOrder: "05/12/2024",
        },
    ];
    const defaultCampaignMetrics = {
        subject: "No Campaign Associated",
        campaignType: "—",
        segmentAudience: currentSegment?.title || "—",
        recipients: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        overallStatus: "N/A",
        sentAt: "—",
        actionsDrilldown: "—",
        sent: 0,
        failed: 0,
        consentIssues: 0,
        revenueInfluenced: "—",
    };

    const campaignMetrics =
        currentSegment?.campaignMetrics || defaultCampaignMetrics;

    // For segments, since no direct filter, display all customers or a subset. Here, all for simplicity.
    const segmentCustomers = customerData; // Or filter based on segment logic if available

    const customerColumns = [
        "CUSTOMER",
        "CONTACT",
        "STATUS",
        "ORDERS",
        "TOTAL SPENT",
        "LAST ORDER",
    ];

    // Filter customers based on search term
    const filteredCustomers = segmentCustomers.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <div className="main_title">
                <h1>View Segment: {currentSegment?.title}</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                    <button onClick={() => navigate("/manage-customers")}>
                        Manage Customers
                    </button>{" "}
                    - View Segment
                </p>
            </div>

            {/* Segment Details */}
            <div className="cards">
                <div className="gap_p view_page">
                    <div className="w_33">
                        <h6>Segment Title</h6>
                        <p>{currentSegment?.icon} {currentSegment?.title}</p>
                    </div>
                    <div className="w_33">
                        <h6>Type</h6>
                        <p>{currentSegment?.type}</p>
                    </div>
                    <div className="w_33">
                        <h6>Count</h6>
                        <p>{currentSegment?.count}</p>
                    </div>
                    <div className="w_33">
                        <h6>Status</h6>
                        <p>{currentSegment?.status}</p>
                    </div>
                    <div className="w_100">
                        <h6>Description</h6>
                        <p>{currentSegment?.description}</p>
                    </div>
                </div>
            </div>

            {/* Campaign Metrics */}
            {/* <div className="cards">
                <div className="gap_p view_page">
                    <div className="w_33">
                        <h6>Subject</h6>
                        <p>{campaignMetrics.subject}</p>
                    </div>

                    <div className="w_33">
                        <h6>Type</h6>
                        <p>{campaignMetrics.campaignType}</p>
                    </div>

                    <div className="w_33">
                        <h6>Segment / Audience</h6>
                        <p>{campaignMetrics.segmentAudience}</p>
                    </div>

                    <div className="w_33">
                        <h6>Recipients</h6>
                        <p>{campaignMetrics.recipients}</p>
                    </div>

                    <div className="w_33">
                        <h6>Delivered</h6>
                        <p>{campaignMetrics.delivered}</p>
                    </div>

                    <div className="w_33">
                        <h6>Opened / CTR</h6>
                        <p>{campaignMetrics.opened} / {campaignMetrics.clicked}</p>
                    </div>

                    <div className="w_33">
                        <h6>Status</h6>
                        <p>{campaignMetrics.overallStatus}</p>
                    </div>

                    <div className="w_33">
                        <h6>Sent At</h6>
                        <p>{campaignMetrics.sentAt}</p>
                    </div>

                    <div className="w_33">
                        <h6>Actions Drill-down</h6>
                        <p>{campaignMetrics.actionsDrilldown}</p>
                    </div>

                    <div className="w_33">
                        <h6>Sent</h6>
                        <p>{campaignMetrics.sent}</p>
                    </div>

                    <div className="w_33">
                        <h6>Failed</h6>
                        <p>{campaignMetrics.failed}</p>
                    </div>

                    <div className="w_33">
                        <h6>Consent issues</h6>
                        <p>{campaignMetrics.consentIssues} blocked</p>
                    </div>

                    <div className="w_33">
                        <h6>Revenue influenced</h6>
                        <p>{campaignMetrics.revenueInfluenced}</p>
                    </div>
                </div>
            </div> */}


            {/* Customers Table */}
            <div className="cards">
                <div className="table_header">
                    <div className="left_s">
                        <Box className="search_box">
                            <SearchBar
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={setSearchTerm}
                                placeholder="Search customers..."
                            />
                        </Box>
                    </div>
                    <div className="right_s">
                        <p>Total Customers: {filteredCustomers.length}</p>
                    </div>
                </div>

                <TableContainer className="table_container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                {customerColumns.map((column, index) => (
                                    <TableCell key={index}>{column}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCustomers.map((customer, index) => (
                                <TableRow key={customer.id} hover>
                                    <TableCell>
                                        <div className="user_block">
                                            <p>
                                                <b>{customer.name}</b>
                                                <span>{customer.id}</span>
                                            </p>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <p>{customer.email}</p>
                                        <span className="sub_text">{customer.phone}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span className={`badge1 ${customer.status.toLowerCase()}`}>
                                            {customer.status}
                                        </span>
                                    </TableCell>
                                    <TableCell>{customer.orders}</TableCell>
                                    <TableCell>{customer.totalSpent}</TableCell>
                                    <TableCell>{customer.lastOrder}</TableCell>

                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </>
    );
};

export default ViewSegment;
